import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { feedback } from '@ecosystem/newron-design-system';
import { AuthenticationProvider } from '@ecosystem/newron-client-lib';
import { ContentProviderRegister, SavedFiltersProvider } from '@ecosystem/newron-core';
import { SpecialStatic } from '../content/special.static';
import { MarketSelector } from '../context/markets';
import { BrandOptionsSelector } from '../context/brands';
import { MarketContextSelector } from '../context/market/market';
import { FocusPeriodContextSelector } from '../context/focusPeriod/focusPeriod';
import { filterContext } from '../filters/filters';
import { ViewSavedFilterHandler } from '../filters/viewSavedFilterHandler';
import { CollectionProvider } from '../providers/collection.provider';
import { addSavedViewKeyToSessionStorage } from '../utils/savedView';
import { ROUTE_PREFIX } from '../utils/constants';
import DPOverviewPage from '../pages/dp/overview';
import { MobileProvider } from '../providers/mobile/mobile.provider';
import { registerDataUpdatesPreferences } from '../notifications/registerPreferences';

const OverviewPage = React.lazy(() => import('../pages/market/overview'));
const MiPage = React.lazy(() => import('../pages/market/mi'));
const ProductsPage = React.lazy(() => import('../pages/market/products'));
const AnalyseProductsPage = React.lazy(() => import('../pages/market/analyse.products'));
const AnalyseLeaderboardPage = React.lazy(() => import('../pages/market/analyse.leaderboard'));

const DPiPage = React.lazy(() => import('../pages/dp/dpi'));
const AnalyseDPProductsPage = React.lazy(() => import('../pages/dp/analyse.products'));
const DPProductsPage = React.lazy(() => import('../pages/dp/products'));
const AnalyseDPLeaderboardPage = React.lazy(() => import('../pages/dp/analyse.leaderboard'));

if (localStorage.getItem('v2context') === 'true') {
  ContentProviderRegister.getInstance().registerContextSettingSelector(MarketContextSelector, 'openMarkets');
} else {
  ContentProviderRegister.getInstance().registerContextSettingSelector(MarketSelector, 'openMarkets');
}
ContentProviderRegister.getInstance().registerContextSettingSelector(FocusPeriodContextSelector, 'focusPeriod');
ContentProviderRegister.getInstance().registerContextSettingSelector(BrandOptionsSelector, 'brandLimitingOptions');
ContentProviderRegister.getInstance().registerContent(SpecialStatic, 'market-custom');

// check if it is copy link view and save it in session storage will be removed after restoring the view
addSavedViewKeyToSessionStorage();

const { Mo, Ml, Mi, Dpi, Dpl, Dpo } = ROUTE_PREFIX;
function Root() {
  useEffect(() => {
    registerDataUpdatesPreferences();
  }, []);
  return (
    <AuthenticationProvider isGuard>
      <SavedFiltersProvider filterContext={filterContext}>
        <MobileProvider>
          <CollectionProvider>
            <React.Suspense fallback={<feedback.DotsLoader stretch height="100vh" />}>
              <BrowserRouter>
                <ViewSavedFilterHandler />
                <Routes>
                  {/* Market routes */}
                  <Route path={Mo} element={<OverviewPage />} />
                  <Route path={`${Ml}/products`} element={<ProductsPage />} />
                  <Route path={`${Ml}/products/analyse/:tab`} element={<AnalyseProductsPage />} />
                  <Route path={`${Mi}/:page/:category`} element={<MiPage />} />
                  <Route path={`${Mi}/:page/:category/analyse/:tab`} element={<AnalyseLeaderboardPage />} />
                  <Route path={Mi} element={<Navigate to="scorecard/brands" replace={true} />} />
                  {/* Distribution Panel routes */}
                  <Route path={Dpo} element={<DPOverviewPage />} />
                  <Route path={`${Dpl}/products`} element={<DPProductsPage />} />
                  <Route path={`${Dpl}/products/analyse/:tab`} element={<AnalyseDPProductsPage />} />
                  <Route path={`${Dpi}/:page/:category`} element={<DPiPage />} />
                  <Route path={`${Dpi}/:page/:category/analyse/:tab`} element={<AnalyseDPLeaderboardPage />} />
                  <Route path={Dpi} element={<Navigate to="scorecard/brands" replace={true} />} />
                  {/* route to prevent console warning on no path match */}
                  <Route path="*" element={<Noop />} />
                </Routes>
              </BrowserRouter>
            </React.Suspense>
          </CollectionProvider>
        </MobileProvider>
      </SavedFiltersProvider>
    </AuthenticationProvider>
  );
}

function Noop() {
  return null;
}

export { Root };
